import React, { useEffect, useState } from 'react';
import { EuiButtonEmpty, EuiText } from '@elastic/eui';
import { TranslateLanguage } from './KeywordTranslator';

const referenceCache = new Map();
const rawValue = (result, field) => result?.[field]?.raw ?? result?.[field]?.snippet ?? '';
const toIds = value => (Array.isArray(value) ? value : String(value || '').split(','))
  .map(item => Number(String(item).trim()))
  .filter(Number.isFinite);

const loadReferences = async (verseId, signal) => {
  const cacheKey = String(verseId || '');
  if (!cacheKey) return [];
  if (referenceCache.has(cacheKey)) return referenceCache.get(cacheKey);

  const crossReferenceApiURL = process.env.REACT_APP_CrossReferenceApiURL;
  const bibleApiURL = process.env.REACT_APP_BibleApiURL;
  const apiKey = process.env.REACT_APP_APIKey;
  if (!crossReferenceApiURL || !bibleApiURL) return [];

  const headers = {
    'Content-Type': 'application/json, charset=UTF-8',
    Authorization: `Bearer search-${apiKey}`,
  };
  const crossResponse = await fetch(crossReferenceApiURL, {
    method: 'POST',
    headers,
    signal,
    body: JSON.stringify({
      query: cacheKey,
      filters: { all: [{ verse_id: cacheKey }] },
      page: { size: 1 },
      sort: { id: 'asc' },
    }),
  });
  if (!crossResponse.ok) throw new Error('Unable to load cross references.');
  const crossData = await crossResponse.json();
  const ids = toIds(crossData?.results?.[0]?.verse_refid?.raw)
    .filter(id => String(id) !== cacheKey);
  if (!ids.length) {
    referenceCache.set(cacheKey, []);
    return [];
  }

  const bibleResponse = await fetch(bibleApiURL, {
    method: 'POST',
    headers,
    signal,
    body: JSON.stringify({
      query: '',
      filters: { any: [{ verse_id: ids }] },
      page: { size: Math.min(ids.length, 1000) },
      sort: { id: 'asc' },
    }),
  });
  if (!bibleResponse.ok) throw new Error('Unable to load referenced verses.');
  const bibleData = await bibleResponse.json();
  const references = (bibleData?.results || []).map(result => {
    const chapter = rawValue(result, 'chapter_number');
    const chapterAndVerse = String(rawValue(result, 'chapter_verse_number') || '');
    const verseRange = String(rawValue(result, 'verse_number') || chapterAndVerse.split(':').pop() || '');
    const bookname = rawValue(result, 'chapter_name');
    const shortBookName = rawValue(result, 'chapter_code_name') || bookname;
    return {
      id: rawValue(result, 'verse_id') || rawValue(result, 'id'),
      book: shortBookName || rawValue(result, 'book_number'),
      bookname,
      chapter: String(chapter),
      verseRange,
      label: `${shortBookName}${chapter}:${verseRange}`,
    };
  }).filter(reference => reference.book && reference.chapter && reference.verseRange);

  referenceCache.set(cacheKey, references);
  return references;
};

export default function InlineCrossReferences({ verseId, language, onReferenceClick, collapsedCount = 6 }) {
  const [references, setReferences] = useState([]);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    loadReferences(verseId, controller.signal)
      .then(setReferences)
      .catch(error => {
        if (error?.name !== 'AbortError') setReferences([]);
      });
    return () => controller.abort();
  }, [verseId]);

  if (!references.length) return null;
  const visible = expanded ? references : references.slice(0, collapsedCount);

  return (
    <div className="inline-cross-references">
      <EuiText size="s">
        <strong>{TranslateLanguage(language, '相關經文：')}</strong>
      </EuiText>
      <div className="inline-cross-reference-links">
        {visible.map(reference => (
          <EuiButtonEmpty
            key={`${reference.id}-${reference.label}`}
            size="xs"
            flush="left"
            onClick={() => onReferenceClick(reference)}
          >
            {TranslateLanguage(language, reference.label)}
          </EuiButtonEmpty>
        ))}
        {references.length > collapsedCount && (
          <EuiButtonEmpty
            size="xs"
            iconType={expanded ? 'arrowUp' : 'arrowDown'}
            onClick={() => setExpanded(value => !value)}
          >
            {TranslateLanguage(language, expanded ? '收起' : `展開全部（${references.length}）`)}
          </EuiButtonEmpty>
        )}
      </div>
    </div>
  );
}
